/**
 * The session's GitHub scope, rendered as an additive `environment_context`
 * contribution to the host's system prompt.
 *
 * A sandbox chat started from the unified composer is bound to an owner (and
 * sometimes a repository) that the agent has no other way to learn: nothing in
 * the working directory or the token names it. Without these facts the agent
 * asks the user which owner to use and retries resources its credentials can
 * never reach.
 *
 * Only the facts are stated. Behaviour is deliberately left to the model —
 * telling it how to respond here would duplicate (and eventually contradict)
 * the runtime's own instructions.
 */

/** Section id the host accepts for "where this session runs" content. */
const ENVIRONMENT_CONTEXT_SECTION = 'environment_context'

/** The session's GitHub scope as resolved when the task was created. */
export interface RelaySessionScope {
  /** Owner login the session's credentials are limited to. */
  owner: string
  /** Repository (name-with-owner) checked out for the session, when there is one. */
  repositoryNwo?: string
}

/** An additive system-prompt contribution, matching `config.systemMessageSections`. */
export interface SystemMessageSectionOverride {
  action: 'append'
  content: string
}

function scopeContent({owner, repositoryNwo}: RelaySessionScope): string {
  if (repositoryNwo) {
    return [
      '## Session scope',
      `This session is scoped to the repository \`${repositoryNwo}\`.`,
      `Your GitHub credentials only reach \`${repositoryNwo}\` and other resources owned by \`${owner}\`.`,
    ].join('\n')
  }
  return [
    '## Session scope',
    `This session is scoped to the GitHub account \`${owner}\` (a user or an organization). No repository is checked out.`,
    `Your GitHub credentials only reach resources owned by \`${owner}\`.`,
  ].join('\n')
}

/**
 * Build the `config.systemMessageSections` value for {@link scope}, or
 * `undefined` when there is no scope worth stating.
 */
export function buildSessionScopeSections(
  scope: RelaySessionScope | undefined,
): Record<string, SystemMessageSectionOverride> | undefined {
  const owner = scope?.owner.trim()
  if (!owner) return undefined
  const repositoryNwo = scope?.repositoryNwo?.trim()
  return {
    [ENVIRONMENT_CONTEXT_SECTION]: {
      action: 'append',
      content: scopeContent({owner, ...(repositoryNwo ? {repositoryNwo} : {})}),
    },
  }
}

/**
 * Resolve the owner a session's credentials are limited to, from the two shapes
 * the composer produces: a selected repository, or a bare scope login.
 *
 * Returns `undefined` when neither yields a login, so callers can omit the
 * scope rather than assert something they cannot back up.
 */
export function resolveSessionScope(
  repositoryNwo: string | undefined,
  scopeLogin: string | undefined,
): RelaySessionScope | undefined {
  const nwo = repositoryNwo?.trim()
  const owner = nwo?.split('/')[0]?.trim() || scopeLogin?.trim()
  if (!owner) return undefined
  return {owner, ...(nwo ? {repositoryNwo: nwo} : {})}
}
